import { FC, useState } from 'react';
import { useSubscription } from 'observable-hooks';

import {
	PassengerBookingActionTypes,
	removeBooking,
	TRemoveBookingResultAction,
	useBusinessLayer,
} from '@mopla/business-logic';
import { PassengerBookingView } from '@mopla/data-models';
import { useTranslation } from '@mopla/i18';

import {
	CompleteAnim,
	MplButton,
	MplErrorModal,
	MplModal,
	usePassengerRoutingHelpers,
} from '../../../';

import { ConfirmationModal } from './ConfirmationModal';

interface IRemoveBookingFlowProps {
	bookedItinerary: PassengerBookingView;
	onClose: VoidFunction;
}

export const RemoveBookingFlow: FC<IRemoveBookingFlowProps> = ({
	bookedItinerary,
	onClose,
}) => {
	const [isDisposeModalOpened, setIsDisposeModalOpened] = useState(true);
	const [isCancelling, setIsCancelling] = useState(false);
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [isSuccess, setSuccess] = useState(false);
	const { navigateToTickets } = usePassengerRoutingHelpers();
	const bl = useBusinessLayer();
	const { t } = useTranslation('tickets');

	const processRemoveBooking = () => {
		bl.dispatch(removeBooking(bookedItinerary));
		setIsCancelling(true);
	};

	useSubscription(
		bl.watchActions$(PassengerBookingActionTypes.RemoveBookingResult),
		(action) => {
			setIsDisposeModalOpened(false);
			setIsCancelling(false);

			if ((action as TRemoveBookingResultAction).payload?.error) {
				setShowErrorModal(true);
			} else {
				setSuccess(true);
				navigateToTickets(undefined, { replace: true });
			}
		}
	);

	return (
		<>
			<MplErrorModal
				open={showErrorModal}
				testId="RemoveBookingFlow-MplErrorModal"
				buttons={
					<MplButton
						fullWidth
						onClick={() => {
							setShowErrorModal(false);
							onClose();
						}}
					>
						{t('errors:button.close')}
					</MplButton>
				}
			/>
			<ConfirmationModal
				open={isDisposeModalOpened}
				onClose={onClose}
				onConfirm={processRemoveBooking}
				isCanceling={isCancelling}
			/>
			<MplModal
				open={isSuccess}
				testId="RemoveBookingFlow-MplModal"
				icon={<CompleteAnim />}
				title={t('cancellation_flow.remove_success.title')}
				buttons={
					<MplButton
						fullWidth
						onClick={onClose}
						testId="RemoveBookingFlow-MplModal-OkButton"
					>
						{t('common:button.ok')}
					</MplButton>
				}
			/>
		</>
	);
};
